// Cookie consent utilities for Google Consent Mode v2 + Meta Pixel gating
// Cookie 'th_consent' stores 'granted' | 'denied' for 180 days.

export type ConsentState = "granted" | "denied";

const COOKIE_NAME = "th_consent";
const MAX_AGE_DAYS = 180;

export const CONSENT_CHANGE_EVENT = "th-consent-change";

declare global {
  interface Window {
    __thConsent?: ConsentState;
    fbq?: any;
    _fbq?: any;
  }
}

export function getConsent(): ConsentState | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"),
  );
  if (!match) return undefined;
  const v = decodeURIComponent(match[1]);
  return v === "granted" || v === "denied" ? v : undefined;
}

export function setConsentCookie(state: ConsentState) {
  if (typeof document === "undefined") return;
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=${state}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

export function clearConsentCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export function isProductionDomain(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "taxhealth.com.br" || h === "www.taxhealth.com.br";
}

function ensureGtag() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
}

function initMetaPixel() {
  if (typeof window === "undefined") return;
  if (!isProductionDomain()) return;
  if (window.fbq && (window.fbq as any).__inited) return;

  // Standard Meta Pixel snippet
  /* eslint-disable */
  (function (f: any, b: any, e: any, v: any) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e);
    t.async = !0;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js",
  );
  /* eslint-enable */
  window.fbq!("init", "1519302356024760");
  window.fbq!("track", "PageView");
  (window.fbq as any).__inited = true;
}

export function applyConsent(state: ConsentState) {
  ensureGtag();
  window.__thConsent = state;
  const value = state === "granted" ? "granted" : "denied";
  window.gtag!("consent", "update", {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
  if (state === "granted") {
    initMetaPixel();
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: state }));
  }
}

export function setConsent(state: ConsentState) {
  setConsentCookie(state);
  applyConsent(state);
}

export function resetConsent() {
  clearConsentCookie();
  window.__thConsent = undefined;
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: undefined }));
}
